'use client';

import { useUser } from '@/lib/auth/use-user';
import Link from 'next/link';
import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ShowIfAuthenticated from '@/components/auth/showIfAuthenticated';
import type { Notification } from '@/types/notifications';
import AdminDropdown from '../../ui/AdminDropdown';
import ManageDropdown from '../../ui/ManageDropdown';
import MobileMenu from '../../ui/MobileMenu';
import NotificationsDropdown from '../../ui/NotificationsDropdown';
import OperationsDropdown from '../../ui/OperationsDropdown';
import UserDropdown from '../../ui/UserDropdown';

interface ClientHeaderProps {
  nickname?: string;
  notifications: Notification[];
  unreadCount: number;
  totalCount: number;
  children: ReactNode;
}

// Profile image cache with localStorage persistence
// Presigned URLs last 1 hour, cache for 50 minutes to be safe
const PROFILE_IMAGE_CACHE_TTL = 50 * 60 * 1000; // 50 minutes
const CACHE_KEY_PREFIX = 'profile_image_cache_';

interface CachedProfileImage {
  url: string | null;
  s3Key: string | null;
  timestamp: number;
}

// In-memory cache for fast access
const profileImageCache = new Map<string, CachedProfileImage>();

// Load from localStorage on initialization
function loadCacheFromStorage(email: string): CachedProfileImage | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${email}`);
    if (!cached) return undefined;

    const parsed: CachedProfileImage = JSON.parse(cached);

    // Check if still valid
    if (Date.now() - parsed.timestamp < PROFILE_IMAGE_CACHE_TTL) {
      return parsed;
    } else {
      // Expired, remove it
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${email}`);
      return undefined;
    }
  } catch (error) {
    console.error(
      '[ClientHeader] Error loading cache from localStorage:',
      error,
    );
    return undefined;
  }
}

// Save to localStorage
function saveCacheToStorage(email: string, data: CachedProfileImage): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(`${CACHE_KEY_PREFIX}${email}`, JSON.stringify(data));
  } catch (error) {
    console.error('[ClientHeader] Error saving cache to localStorage:', error);
  }
}

// Clear cache for a specific user (can be called when profile image is updated)
export function clearProfileImageCache(email: string): void {
  profileImageCache.delete(email);
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${email}`);
      console.log('[ClientHeader] ✅ Profile image cache cleared for:', email);
    } catch (error) {
      console.error(
        '[ClientHeader] Error clearing cache from localStorage:',
        error,
      );
    }
  }
}

// Clear all profile image caches
export function clearAllProfileImageCaches(): void {
  profileImageCache.clear();
  if (typeof window !== 'undefined') {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      console.log('[ClientHeader] ✅ All profile image caches cleared');
    } catch (error) {
      console.error(
        '[ClientHeader] Error clearing all caches from localStorage:',
        error,
      );
    }
  }
}

export default function ClientHeader({
  nickname,
  notifications,
  unreadCount,
  totalCount,
  children,
}: ClientHeaderProps) {
  // Use Auth0 client user
  const { user } = useUser();
  const [isMounted, setIsMounted] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [imageFetched, setImageFetched] = useState(false);

  // Initialize from cache on mount
  useEffect(() => {
    setIsMounted(true);

    // Try to load from cache immediately on mount
    if (user?.email) {
      const userEmail = user.email;

      // Check in-memory cache first
      let cached = profileImageCache.get(userEmail);

      // If not in memory, try localStorage
      if (!cached) {
        cached = loadCacheFromStorage(userEmail);
        if (cached) {
          // Populate in-memory cache from localStorage
          profileImageCache.set(userEmail, cached);
          console.log('[ClientHeader] ⚡ Loaded from localStorage cache');
        }
      } else {
        console.log('[ClientHeader] ⚡ Loaded from memory cache');
      }

      // Use cached value if valid
      if (cached && Date.now() - cached.timestamp < PROFILE_IMAGE_CACHE_TTL) {
        setProfileImageUrl(cached.url);
        setImageFetched(true); // Mark as fetched to skip API call
      }
    }
  }, [user?.email]);

  // Fetch profile image from database ONCE with caching
  useEffect(() => {
    if (!user || !user.email || imageFetched) return;

    const userEmail = user.email;

    async function fetchProfileImage() {
      try {
        setImageFetched(true); // Mark as fetched to prevent re-fetching
        console.log('[ClientHeader] 🔄 Fetching profile for user:', userEmail);
        const response = await fetch('/api/profile');

        if (response.ok) {
          const data = await response.json();

          const profileImageKey = data.profile?.profile_image;
          if (profileImageKey && profileImageKey.trim() !== '') {
            console.log(
              '[ClientHeader] Profile image S3 key:',
              profileImageKey,
            );

            // Check if we have a cached URL for this exact S3 key
            const cached = profileImageCache.get(userEmail);
            if (
              cached &&
              cached.s3Key === profileImageKey &&
              Date.now() - cached.timestamp < PROFILE_IMAGE_CACHE_TTL
            ) {
              console.log(
                '[ClientHeader] ⚡ Reusing cached URL for same S3 key',
              );
              setProfileImageUrl(cached.url);
              return;
            }

            // Get presigned URL for the profile image
            const imageResponse = await fetch(
              `/api/profile/image?key=${encodeURIComponent(profileImageKey)}`,
            );

            if (imageResponse.ok) {
              const imageData = await imageResponse.json();
              console.log(
                '[ClientHeader] ✅ Presigned URL fetched and cached for 50 minutes',
              );
              setProfileImageUrl(imageData.url);

              // Cache both in memory and localStorage
              const cacheData: CachedProfileImage = {
                s3Key: profileImageKey,
                timestamp: Date.now(),
                url: imageData.url,
              };
              profileImageCache.set(userEmail, cacheData);
              saveCacheToStorage(userEmail, cacheData);
            } else {
              console.error('[ClientHeader] Failed to get presigned URL');
              setProfileImageUrl(null);
              const cacheData: CachedProfileImage = {
                s3Key: profileImageKey,
                timestamp: Date.now(),
                url: null,
              };
              profileImageCache.set(userEmail, cacheData);
              saveCacheToStorage(userEmail, cacheData);
            }
          } else {
            console.log('[ClientHeader] No profile_image in database');
            setProfileImageUrl(null);
            const cacheData: CachedProfileImage = {
              s3Key: null,
              timestamp: Date.now(),
              url: null,
            };
            profileImageCache.set(userEmail, cacheData);
            saveCacheToStorage(userEmail, cacheData);
          }
        } else {
          console.error('[ClientHeader] Profile API error');
          setProfileImageUrl(null);
        }
      } catch (error) {
        console.error('[ClientHeader] Failed to fetch profile image:', error);
        setProfileImageUrl(null);
      }
    }

    fetchProfileImage();
  }, [user, imageFetched]);

  // Get the portal target element (only once when mounted)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Wait for DOM to be ready and look for client-actions element
    if (isMounted) {
      const clientActionsElement = document.getElementById('client-actions');
      setPortalTarget(clientActionsElement);
    }
  }, [isMounted]);

  // Create portal content (React will handle updates without recreating the portal)
  const actionsPortal = portalTarget
    ? createPortal(
        <>
          {/* Desktop-only items */}
          <div className="hidden lg:flex lg:items-center lg:space-x-2">
            <ShowIfAuthenticated
              fallback={
                <Link
                  aria-label="Sign in to your account"
                  className="rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white hover:bg-purple-800 focus:outline-none focus:ring-4 focus:ring-purple-300 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-800"
                  href="/login"
                >
                  Sign In
                </Link>
              }
            >
              <NotificationsDropdown
                notifications={notifications}
                totalCount={totalCount}
                unreadCount={unreadCount}
              />
              <OperationsDropdown />
              <ManageDropdown />
              <AdminDropdown />
              <UserDropdown
                nickname={nickname}
                profileImageUrl={profileImageUrl}
                user={user}
              />
            </ShowIfAuthenticated>
          </div>

          <MobileMenu profileImageUrl={profileImageUrl} user={user} />
        </>,
        portalTarget,
      )
    : null;

  // Render the server component with the client portal
  return (
    <>
      {children}
      {actionsPortal}
    </>
  );
}
